import {ProcedureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {EXPERIMENT_VALIDATION_CONSTANTS} from 'studies/StudyConstants';
import {ConditionValidationState} from 'studies/typed/shared/useExperimentDesignState';
import {getProcedureRunConditionTextForProcedureType} from 'studies/TypedStudyUtils';

export interface ConditionDraft {
  numReplicates: number;
  name: string;
  description: string | undefined;
}

export const DEFAULT_CONDITION_VALIDATION_STATE: ConditionValidationState = {
  isDuplicateName: false,
  isNameLengthOverLimit: false,
  isDescLengthOverLimit: false,
};

/**
 * Computes a per-row validation state given the full set of (potentially renamed) names. Used
 * by both shape variants in replicateTableUtils; extracted so the duplicate-name detection only
 * enumerates the name set once per call.
 */
export function buildValidationStatesForUpdate(
  newConditions: ReadonlyArray<ConditionDraft>
): Array<ConditionValidationState> {
  const conditionCounts = getConditionCountsByName(newConditions.map((c) => c.name));
  return newConditions.map((condition) => ({
    isDuplicateName: conditionCounts.get(condition.name)! > 1,
    isNameLengthOverLimit: condition.name.length > EXPERIMENT_VALIDATION_CONSTANTS.CONDITION_NAME_MAX_LENGTH,
    isDescLengthOverLimit:
      (condition.description ?? '').length > EXPERIMENT_VALIDATION_CONSTANTS.CONDITION_DESCRIPTION_MAX_LENGTH,
  }));
}

export function getDefaultConditionNames(length: number, procedureType: ProcedureType): Array<string> {
  return Array.from(
    {length},
    (_, conditionNumber: number) =>
      `${getProcedureRunConditionTextForProcedureType(procedureType, {capital: true})} ${conditionNumber + 1}`
  );
}

/**
 * Returns `numConditionsToGenerate` default condition names that are unique across `existingConditionNames`.
 *
 * Names follow the usual "Condition {x}" format and always pick the lowest available numeric suffix first.
 */
export function getNextDefaultConditionNames(
  existingConditionNames: Array<string>,
  numConditionsToGenerate: number,
  procedureType: ProcedureType
): Array<string> {
  if (numConditionsToGenerate <= 0) {
    return [];
  }
  const unavailableNames = new Set(existingConditionNames);
  const nextConditionNames: Array<string> = [];
  const conditionText = getProcedureRunConditionTextForProcedureType(procedureType, {capital: true});
  let conditionNumber = 1;

  while (nextConditionNames.length < numConditionsToGenerate) {
    const candidateName = `${conditionText} ${conditionNumber}`;
    if (!unavailableNames.has(candidateName)) {
      nextConditionNames.push(candidateName);
      unavailableNames.add(candidateName);
    }
    conditionNumber += 1;
  }
  return nextConditionNames;
}

export function getConditionCountsByName(names: Array<string>): Map<string, number> {
  const conditionCounts = new Map<string, number>();

  names.forEach((name) => {
    if (conditionCounts.has(name)) {
      // Increment count if name already exists
      conditionCounts.set(name, conditionCounts.get(name)! + 1);
    } else {
      // Initialize count if name does not exist
      conditionCounts.set(name, 1);
    }
  });

  return conditionCounts;
}

/**
 * Builds the initial PMDV coverage map for a newly created draft ConditionGroup.
 * When coverage is provided, seed those methods with null ids (covered but unsaved).
 * Otherwise return an empty map, preserving legacy "covers all methods" behavior.
 */
export function buildDraftConditionGroupPmdvMap(
  newConditionCoveredMethodIds?: ReadonlyArray<string>
): Record<string, string | null> {
  return newConditionCoveredMethodIds != null
    ? Object.fromEntries(newConditionCoveredMethodIds.map((methodId) => [methodId, null]))
    : {};
}
